import { z } from 'zod';
import { AuthorRefSchema, IdentityRefSchema, ParticipantRefSchema } from './refs.js';

/** Interaktionsarten ([A] 2.3, `interaction.type`). */
export const INTERACTION_TYPES = [
  'message_sent',
  'message_received',
  'message_edited',
  'message_deleted',
  'reaction_added',
  'reaction_changed',
  'reaction_removed',
  'comment_added',
  'comment_edited',
  'comment_deleted',
  'invitation_sent',
  'invitation_received',
  'connected',
] as const;
export const InteractionTypeSchema = z.enum(INTERACTION_TYPES);
export type InteractionType = z.infer<typeof InteractionTypeSchema>;

/**
 * Umschlag jeder Tatsache ([A] F4). Fehlender Schlüssel = „nicht geliefert“, `null` = „ausdrücklich
 * leer“; deshalb `.exactOptional()`. `sourceTime` ist Pflicht (`null` bei Snapshots).
 */
const envelopeShape = {
  op: z.enum(['upsert', 'patch', 'delete']),
  sourceEventId: z.string().min(1),
  resourceId: z.string().exactOptional(),
  sourceTime: z.date().nullable(),
};

/** Fakten sind strikt: kein ungeplantes Feld (etwa Rohtext) kommt durch. */
const PersonFactSchema = z.strictObject({
  ...envelopeShape,
  kind: z.literal('person'),
  identity: IdentityRefSchema,
  fields: z.strictObject({
    firstName: z.string().nullable().exactOptional(),
    lastName: z.string().nullable().exactOptional(),
    position: z.string().nullable().exactOptional(),
    company: z.string().nullable().exactOptional(),
  }),
});

const ConnectionFactSchema = z.strictObject({
  ...envelopeShape,
  kind: z.literal('connection'),
  identity: IdentityRefSchema,
  connectedOn: z.iso.date(),
});

const ConversationFactSchema = z.strictObject({
  ...envelopeShape,
  kind: z.literal('conversation'),
  externalId: z.string(),
  participants: z.array(ParticipantRefSchema),
  title: z.string().nullable().exactOptional(),
  folder: z.enum(['INBOX', 'ARCHIVE', 'SPAM']).exactOptional(),
});

const MessageFactSchema = z.strictObject({
  ...envelopeShape,
  kind: z.literal('message'),
  conversationExternalId: z.string(),
  externalId: z.string().exactOptional(),
  author: AuthorRefSchema,
  sentAt: z.date().exactOptional(),
  eventTime: z.date().nullable(),
  text: z.string().exactOptional(),
});

const InteractionFactSchema = z.strictObject({
  ...envelopeShape,
  kind: z.literal('interaction'),
  type: InteractionTypeSchema,
  occurredAt: z.date(),
  counterpart: IdentityRefSchema.exactOptional(),
  objectRef: z.string().exactOptional(),
});

const InvitationFactSchema = z.strictObject({
  ...envelopeShape,
  kind: z.literal('invitation'),
  direction: z.enum(['sent', 'received']),
  counterpart: IdentityRefSchema,
  sentAt: z.date(),
});

/** [TP5] 4.4: Zeitpunkte bleiben ISO-Zeichenketten mit Offset (Vereinheitlichung offen, R-12). */
const OwnPostFactSchema = z.strictObject({
  ...envelopeShape,
  op: z.enum(['upsert', 'delete']),
  kind: z.literal('own_post'),
  postUrl: z.string().exactOptional(),
  activityUrn: z.string().exactOptional(),
  shareUrn: z.string().exactOptional(),
  publishedAt: z.iso.datetime({ offset: true }),
  visibility: z.enum(['PUBLIC', 'CONNECTIONS', 'UNKNOWN']).exactOptional(),
  text: z.string().exactOptional(),
  isRepost: z.boolean(),
});

const OwnCommentFactSchema = z.strictObject({
  ...envelopeShape,
  kind: z.literal('own_comment'),
  objectRef: z.string(),
  commentedAt: z.iso.datetime({ offset: true }),
  text: z.string().exactOptional(),
});

export const NormalizedFactSchema = z.discriminatedUnion('kind', [
  PersonFactSchema,
  ConnectionFactSchema,
  ConversationFactSchema,
  MessageFactSchema,
  InteractionFactSchema,
  InvitationFactSchema,
  OwnPostFactSchema,
  OwnCommentFactSchema,
]);
export type NormalizedFact = z.infer<typeof NormalizedFactSchema>;

export const NormalizeResultSchema = z.discriminatedUnion('status', [
  z.strictObject({ status: z.literal('ok'), facts: z.array(NormalizedFactSchema) }),
  z.strictObject({
    status: z.literal('ignored'),
    reason: z.enum(['unknown_resource', 'activity_failure']),
  }),
  z.strictObject({
    status: z.literal('error'),
    reason: z.enum(['unknown_shape', 'invalid_field']),
    path: z.string().min(1),
  }),
]);
export type NormalizeResult = z.infer<typeof NormalizeResultSchema>;
